// src/services/userService.js
// Couche service — toutes les opérations Firebase

import { auth, db, storage, functions } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  updateDoc,
  collection,
  query,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { calculateAge, getDisplayGender, getDistance } from '../utils';

// Helper : lecture serveur Firestore avec fallback cache (hors-ligne)
const getDocFresh = async (ref) => {
  try {
    return await getDocFromServer(ref);
  } catch {
    return await getDoc(ref);
  }
};

const userService = {
  // ── Authentification ──────────────────────────────────
  async register(email, password) {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(cred.user);
      const u = {
        uid: cred.user.uid,
        email,
        createdAt: serverTimestamp(),
        name: '',
        points: 0,
        onboardingComplete: false,

        // Champs initialisés vides — remplis par l'onboarding
        clan: '',
        dateOfBirth: null,
        gender: '',
        city: '',
        cityPostalCode: '',
        cityLat: null,
        cityLng: null,
        mode: '',
        friends: [],
        motivations: [],
        interests: [],
        datingEnabled: false,
        lookingFor: null,
        intention: null,
        height: null,
        education: null,
        profession: '',
        languages: [],
        datingPhotos: [],
        datingPreferences: {
          ageRange: [18, 50],
          maxDistance: null,
          heightRange: null,
          intentions: [],
          education: null,
        },
        bioPrompts: [],
        profilePicture: '',
        coverPicture: '',
        gallery: [],
        verified: false,
        badges: ['Nouveau'],
        premium: false,
        premiumUntil: null,
        likes: [],
        matches: [],
        blocked: [],
        dailyLikes: 0,
        dailyLikesResetAt: null,
        online: true,
        lastSeen: serverTimestamp(),
      };
      await setDoc(doc(db, 'users', cred.user.uid), u);
      return { success: true, user: u, emailVerified: false };
    } catch (e) {
      const msgs = {
        'auth/email-already-in-use': 'Email déjà utilisé',
        'auth/weak-password': 'Mot de passe trop faible (8 caractères minimum)',
        'auth/invalid-email': 'Email invalide',
      };
      return { success: false, error: msgs[e.code] || 'Erreur inscription' };
    }
  },

  async login(email, password) {
    // 1. Authentification — seule étape pouvant légitimement échouer
    let cred;
    try {
      cred = await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error('userService.login auth error:', err);
      return { success: false, error: 'Email ou mot de passe incorrect' };
    }

    if (!cred.user.emailVerified) {
      return { success: true, emailVerified: false, user: null };
    }

    // 2. Chargement profil — si Firestore échoue, auth a quand même réussi
    //    onAuthStateChanged dans App.js prendra le relai
    try {
      const snap = await getDocFresh(doc(db, 'users', cred.user.uid));
      if (snap.exists()) {
        updateDoc(doc(db, 'users', cred.user.uid), {
          online: true,
          lastSeen: serverTimestamp(),
        }).catch(() => {});
        return { success: true, emailVerified: true, user: { uid: cred.user.uid, ...snap.data() } };
      }
    } catch (err) {
      console.error('userService.login profile error:', err);
    }

    return { success: true, emailVerified: true, user: null };
  },

  async resendVerification() {
    try {
      if (auth.currentUser && !auth.currentUser.emailVerified) {
        await sendEmailVerification(auth.currentUser);
        return { success: true };
      }
      return { success: false, error: 'Aucun utilisateur en attente de vérification' };
    } catch (e) {
      if (e.code === 'auth/too-many-requests') {
        return { success: false, error: 'Trop de tentatives. Réessayez dans quelques minutes.' };
      }
      return { success: false, error: "Erreur lors de l'envoi" };
    }
  },

  async checkEmailVerified() {
    try {
      if (!auth.currentUser) return false;
      await auth.currentUser.reload();
      return auth.currentUser.emailVerified;
    } catch (err) {
      console.error('userService.checkEmailVerified error:', err);
      return false;
    }
  },

  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      console.error('userService.resetPassword error:', err);
      return { success: false, error: err.code || 'unknown' };
    }
  },

  async logout() {
    try {
      if (auth.currentUser) {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          online: false,
          lastSeen: serverTimestamp(),
        });
      }
      await signOut(auth);
      return { success: true };
    } catch (err) {
      console.error('userService.logout error:', err);
      return { success: false };
    }
  },

  async getUser() {
    if (!auth.currentUser) return null;
    try {
      const s = await getDocFresh(doc(db, 'users', auth.currentUser.uid));
      return s.exists() ? { uid: auth.currentUser.uid, ...s.data() } : null;
    } catch (err) {
      console.error('userService.getUser error:', err);
      return null;
    }
  },

  async getProfile(uid) {
    try {
      const s = await getDocFresh(doc(db, 'users', uid));
      return s.exists() ? { uid, ...s.data() } : null;
    } catch (err) {
      console.error('userService.getProfile error:', err);
      return null;
    }
  },

  // ── Profil ────────────────────────────────────────────
  async updateProfile(uid, data) {
    try {
      const {
        clan: _clan,
        uid: _uid,
        email: _email,
        dateOfBirth: _dob,
        createdAt: _ca,
        onboardingComplete: _ob,
        // Champs protégés — modifiés uniquement par Cloud Functions / Admin SDK
        likes: _likes,
        matches: _matches,
        points: _points,
        premium: _premium,
        premiumUntil: _pu,
        dailyLikes: _dl,
        dailyLikesResetAt: _dlr,
        role: _role,
        verified: _verified,
        badges: _badges,
        ...d
      } = data;
      await updateDoc(doc(db, 'users', uid), { ...d, lastSeen: serverTimestamp() });
      const s = await getDocFresh(doc(db, 'users', uid));
      return { success: true, user: { uid, ...s.data() } };
    } catch (err) {
      console.error('userService.updateProfile error:', err);
      return { success: false };
    }
  },

  async uploadPic(uid, file, type = 'profile') {
    try {
      const blob = await new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          let w = img.width,
            h = img.height;
          const max = type === 'cover' ? 800 : 400;
          if (w > h) {
            if (w > max) {
              h = (h * max) / w;
              w = max;
            }
          } else {
            if (h > max) {
              w = (w * max) / h;
              h = max;
            }
          }
          canvas.width = w;
          canvas.height = h;
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(resolve, 'image/jpeg', 0.7);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          reject(new Error('Format image invalide'));
        };
        img.src = URL.createObjectURL(file);
      });
      const sRef = ref(storage, `${type}_pictures/${uid}/${type}_${Date.now()}.jpg`);
      await uploadBytes(sRef, blob);
      const url = await getDownloadURL(sRef);
      await updateDoc(doc(db, 'users', uid), { [type + 'Picture']: url });
      const s = await getDocFresh(doc(db, 'users', uid));
      return { success: true, user: { uid, ...s.data() } };
    } catch (err) {
      console.error('userService.uploadPic error:', err);
      return { success: false, error: "Erreur lors de l'envoi" };
    }
  },

  async _uploadPicAndGetUrl(uid, file) {
    const blob = await new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        let w = img.width,
          h = img.height;
        const max = 400;
        if (w > h) {
          if (w > max) {
            h = (h * max) / w;
            w = max;
          }
        } else {
          if (h > max) {
            w = (w * max) / h;
            h = max;
          }
        }
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(resolve, 'image/jpeg', 0.7);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Format image invalide'));
      };
      img.src = URL.createObjectURL(file);
    });
    const sRef = ref(storage, `profile_pictures/${uid}/profile_${Date.now()}.jpg`);
    await uploadBytes(sRef, blob);
    return getDownloadURL(sRef);
  },

  async completeOnboarding(uid, data) {
    try {
      let profilePicUrl = data.profilePicture || '';
      if (data.profilePictureFile) {
        profilePicUrl = await this._uploadPicAndGetUrl(uid, data.profilePictureFile);
      }

      const updateData = {
        name: data.name,
        dateOfBirth: data.dateOfBirth,
        gender: data.gender,
        clan: data.clan || '',
        city: data.city,
        cityPostalCode: data.cityPostalCode || '',
        cityLat: data.cityLat || null,
        cityLng: data.cityLng || null,
        mode: data.mode || '',
        profilePicture:
          profilePicUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.name}`,
        onboardingComplete: true,
        points: 50,
        lastSeen: serverTimestamp(),
      };

      await updateDoc(doc(db, 'users', uid), updateData);
      const s = await getDocFresh(doc(db, 'users', uid));
      return { success: true, user: { uid, ...s.data() } };
    } catch (err) {
      console.error('userService.completeOnboarding error:', err);
      return { success: false, error: "Erreur lors de la finalisation de l'inscription" };
    }
  },

  async activateDating(uid, data) {
    try {
      // Vérifier l'âge côté service (belt and suspenders)
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const age = calculateAge(userDoc.data().dateOfBirth);
        if (age === null || age < 18) {
          return { success: false, error: 'Vous devez avoir 18 ans pour activer les rencontres.' };
        }
      }

      let profilePicUrl = null;
      if (data.profilePictureFile) {
        profilePicUrl = await this._uploadPicAndGetUrl(uid, data.profilePictureFile);
      }

      const currentMode = userDoc.data().mode;
      const updateData = {
        datingEnabled: true,
        lookingFor: data.lookingFor,
        intention: data.intention,
        lastSeen: serverTimestamp(),
      };
      // Si mode community ou absent → passer en "both"
      if (!currentMode || currentMode === 'community' || currentMode === '') {
        updateData.mode = 'both';
      }
      if (profilePicUrl) {
        updateData.profilePicture = profilePicUrl;
      }

      await updateDoc(doc(db, 'users', uid), updateData);
      const s = await getDocFresh(doc(db, 'users', uid));
      return { success: true, user: { uid, ...s.data() } };
    } catch (err) {
      console.error('userService.activateDating error:', err);
      return { success: false, error: "Erreur lors de l'activation des rencontres" };
    }
  },

  // ── Découverte & Likes ────────────────────────────────
  async getProfiles(user, filters = {}) {
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('lastSeen', 'desc')));
      let list = [];
      snap.forEach((d) => {
        const { email: _email, ...rest } = d.data();
        const p = { uid: d.id, ...rest };
        if (
          !user ||
          (p.uid !== user.uid &&
            (p.clan !== user.clan ||
              !user.clan ||
              !p.clan ||
              user.clan === 'Autre' ||
              p.clan === 'Autre'))
        )
          list.push(p);
      });
      // Filtrer les profils bloqués (bidirectionnel)
      if (user) {
        const userBlocked = user.blocked || [];
        list = list.filter((p) => {
          const pBlocked = p.blocked || [];
          return !userBlocked.includes(p.uid) && !pBlocked.includes(user.uid);
        });
      }

      // Exclure les profils déjà likés
      if (user) {
        const userLikes = user.likes || [];
        list = list.filter((p) => !userLikes.includes(p.uid));
      }

      // Exclure les profils mineurs (sécurité — ne devraient pas avoir datingEnabled, mais au cas où)
      list = list.filter((p) => {
        const profileAge = calculateAge(p.dateOfBirth);
        return profileAge !== null && profileAge >= 18;
      });

      if (filters.ageMin) {
        list = list.filter((p) => {
          const age = calculateAge(p.dateOfBirth) ?? p.age;
          return age !== null && age >= +filters.ageMin;
        });
      }
      if (filters.ageMax) {
        list = list.filter((p) => {
          const age = calculateAge(p.dateOfBirth) ?? p.age;
          return age !== null && age <= +filters.ageMax;
        });
      }
      if (filters.city) list = list.filter((p) => p.city === filters.city);
      if (filters.clan) list = list.filter((p) => p.clan === filters.clan);
      if (filters.gender) {
        list = list.filter((p) => getDisplayGender(p) === filters.gender);
      }
      if (filters.intention) list = list.filter((p) => p.intention === filters.intention);
      if (filters.verifiedOnly) list = list.filter((p) => p.verified);
      if (filters.onlineOnly) list = list.filter((p) => p.online);

      // Pré-filtrage par préférences dating
      if (user) {
        const prefs = user.datingPreferences || {};
        // Filtre par tranche d'âge des préférences
        if (prefs.ageRange && prefs.ageRange.length === 2) {
          list = list.filter((p) => {
            const age = calculateAge(p.dateOfBirth);
            return age === null || (age >= prefs.ageRange[0] && age <= prefs.ageRange[1]);
          });
        }
        // Filtre par distance max
        if (prefs.maxDistance) {
          list = list.filter((p) => {
            const dist = getDistance(user, p);
            return dist === null || dist <= prefs.maxDistance;
          });
        }
        // Filtre par intentions
        if (prefs.intentions && prefs.intentions.length > 0) {
          list = list.filter(
            (p) => !p.intention || prefs.intentions.includes(p.intention) || p.intention === 'open'
          );
        }
      }

      // Strip champs sensibles — ne pas exposer aux autres utilisateurs
      return list.map(
        ({
          blocked: _blocked,
          role: _role,
          dailyLikes: _dl,
          dailyLikesResetAt: _dlr,
          points: _pts,
          likes: _likes,
          matches: _matches,
          premium: _premium,
          premiumUntil: _pu,
          ...rest
        }) => rest
      );
    } catch (err) {
      console.error('userService.getProfiles error:', err);
      return [];
    }
  },

  /**
   * Récupère la liste des membres pour l'annuaire.
   * Strip les champs dating et privés — ne retourne que les infos communautaires.
   */
  async getMembers(currentUser) {
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('lastSeen', 'desc')));
      let list = [];
      snap.forEach((d) => {
        const data = d.data();
        if (!data.onboardingComplete) return;
        if (currentUser && d.id === currentUser.uid) return;
        list.push({ uid: d.id, ...data });
      });

      // Filtrer les profils bloqués (bidirectionnel)
      if (currentUser) {
        const userBlocked = currentUser.blocked || [];
        list = list.filter((p) => {
          const pBlocked = p.blocked || [];
          return !userBlocked.includes(p.uid) && !pBlocked.includes(currentUser.uid);
        });
      }

      // Strip : ne garder que les champs communautaires + profil visible
      return list.map((p) => ({
        uid: p.uid,
        name: p.name,
        profilePicture: p.profilePicture,
        coverPicture: p.coverPicture || null,
        city: p.city,
        clan: p.clan,
        dateOfBirth: p.dateOfBirth,
        gender: p.gender,
        mode: p.mode,
        friends: p.friends,
        online: p.online,
        languages: p.languages || [],
        bio: p.bio || '',
        bioPrompts: p.bioPrompts || [],
        interests: p.interests || [],
        badges: p.badges || [],
      }));
    } catch (err) {
      console.error('userService.getMembers error:', err);
      return [];
    }
  },

  async like(_uid, targetId) {
    try {
      const likeUserFn = httpsCallable(functions, 'likeUser');
      const result = await likeUserFn({ targetId });
      return result.data;
    } catch (err) {
      console.error('userService.like error:', err);
      return { success: false, error: err.message || 'Erreur lors du like' };
    }
  },

  // ── Matches ───────────────────────────────────────────
  async getMatches(uid) {
    try {
      const s = await getDoc(doc(db, 'users', uid));
      const matchIds = s.data()?.matches || [];
      const userBlocked = s.data()?.blocked || [];
      if (matchIds.length === 0) return [];

      const matches = [];
      for (const id of matchIds) {
        if (userBlocked.includes(id)) continue;
        const m = await getDoc(doc(db, 'users', id));
        if (m.exists()) {
          const {
            email: _email,
            blocked: _blocked,
            role: _role,
            dailyLikes: _dl,
            dailyLikesResetAt: _dlr,
            points: _pts,
            likes: _likes,
            matches: _matchList,
            premium: _premium,
            premiumUntil: _pu,
            ...rest
          } = m.data();
          const pBlocked = _blocked || [];
          if (!pBlocked.includes(uid)) {
            matches.push({ uid: id, ...rest });
          }
        }
      }
      return matches;
    } catch (err) {
      console.error('userService.getMatches error:', err);
      return [];
    }
  },

  // ── Suppression de compte (RGPD) ────────────────────
  async deleteAccount(uid) {
    try {
      const { deleteDoc, doc: docRef } = await import('firebase/firestore');
      await deleteDoc(docRef(db, 'users', uid));
      if (auth.currentUser) {
        const { deleteUser } = await import('firebase/auth');
        await deleteUser(auth.currentUser);
      }
      return { success: true };
    } catch (e) {
      if (e.code === 'auth/requires-recent-login') {
        return {
          success: false,
          error:
            'Pour des raisons de sécurité, veuillez vous reconnecter avant de supprimer votre compte.',
        };
      }
      return { success: false, error: 'Erreur lors de la suppression du compte.' };
    }
  },

  // ── Blocage ─────────────────────────────────────────
  async blockUser(uid, targetId) {
    try {
      await updateDoc(doc(db, 'users', uid), {
        blocked: arrayUnion(targetId),
      });
      return { success: true };
    } catch (err) {
      console.error('userService.blockUser error:', err);
      return { success: false, error: err.message };
    }
  },

  async unblockUser(uid, targetId) {
    try {
      await updateDoc(doc(db, 'users', uid), {
        blocked: arrayRemove(targetId),
      });
      return { success: true };
    } catch (err) {
      console.error('userService.unblockUser error:', err);
      return { success: false, error: err.message };
    }
  },

  async getReceivedLikes(uid) {
    try {
      const q = query(
        collection(db, `users/${uid}/receivedLikes`),
        orderBy('createdAt', 'desc'),
        firestoreLimit(50)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error('userService.getReceivedLikes error:', err);
      return [];
    }
  },

  hasLiked: (u, id) => u?.likes?.includes(id) || false,
};

export default userService;
